import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { auth as authApi } from '../api/endpoints';
import { TOKEN_KEY } from '../api/client';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  // Restore the session on a hard refresh.
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setLoading(false);
      return;
    }
    authApi
      .me()
      .then((res) => setAdmin(res.data.admin))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await authApi.login({ email, password });
    localStorage.setItem(TOKEN_KEY, res.data.token);
    setAdmin(res.data.admin);
    return res.data.admin;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setAdmin(null);
    window.location.href = '/login';
  }, []);

  /**
   * The single permission check used across the panel - by the sidebar to decide
   * which links to render, and by pages to decide which buttons to show.
   * The Super Admin passes everything.
   */
  const can = useCallback(
    (...permissions) => {
      if (!admin) return false;
      if (admin.isSuperAdmin) return true;
      const granted = admin.permissions || [];
      return permissions.some((p) => granted.includes(p));
    },
    [admin]
  );

  const value = useMemo(
    () => ({
      admin,
      loading,
      login,
      logout,
      can,
      isSuperAdmin: Boolean(admin?.isSuperAdmin),
      // A store-scoped account only ever works inside its own branch.
      storeId: admin?.store?._id || admin?.store || null,
      storeName: admin?.store?.name || '',
      setAdmin,
    }),
    [admin, loading, login, logout, can]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
};
